import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import { PLAYBOOK_BUILDER_MODE } from '../../../constants/playbookBuilder';

import { useResponsiveBreakpoints } from '../../../hooks/useResponsiveBreakpoints';

import PlaybookDetailsModal from '../PlaybookDetailsModal/PlaybookDetailsModal';
import PlaybookBlocksViewIndex from '../PlaybookBlocksViewIndex/PlaybookBlocksViewIndex';
import PlaybookHeaderMobileView from '../PlaybookHeaderMobileView/PlaybookHeaderMobileView';
import PlaybookBlocksViewTopControls from '../PlaybookBlocksViewTopControls/PlaybookBlocksViewTopControls';

const PlaybookBlocksView = ({
    playbookDetail,
    setPlaybookDetail,
    playbookContentData,
    playbookContentChangesState,
    backLinkHref,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const mode = searchParams.get('mode');

    const location = useLocation();

    const [isPlaybookEditModalOpened, setIsPlaybookEditModalOpened] = useState(false);

    const [pageTopControlsHeight, setPageTopControlsHeight] = useState(40);

    const { isDesktop } = useResponsiveBreakpoints();

    const isPlaybookShared = playbookDetail?.is_shared;

    const pageIndexHeight =
        pageTopControlsHeight && isDesktop ? `calc(100% - ${pageTopControlsHeight}px)` : 'auto';

    useEffect(() => {
        const isWrongViewMode =
            (isPlaybookShared || !isDesktop) && mode !== PLAYBOOK_BUILDER_MODE.view;
        if (isWrongViewMode) {
            changeMode(PLAYBOOK_BUILDER_MODE.view, true);
            return;
        }

        const isParsedModeCorrect = Object.values(PLAYBOOK_BUILDER_MODE).includes(mode);

        if (!isParsedModeCorrect) {
            const defaultMode = PLAYBOOK_BUILDER_MODE.view;
            changeMode(defaultMode, true);
        }
    }, [mode, isDesktop, isPlaybookShared]);

    const changeMode = (newMode, shouldReplace = false) => {
        if (mode === newMode) {
            return;
        }

        // do not allow user to change mode to edit on mobile
        if (!isDesktop && newMode === PLAYBOOK_BUILDER_MODE.edit) {
            return;
        }

        if (isPlaybookShared && newMode === PLAYBOOK_BUILDER_MODE.edit) {
            return;
        }

        const urlSearchParams = new URLSearchParams(searchParams);
        urlSearchParams.set('mode', newMode);
        setSearchParams(urlSearchParams, { replace: shouldReplace, state: location.state });
    };

    const openPlaybookEditModal = useCallback(
        () => setIsPlaybookEditModalOpened(true),
        [setIsPlaybookEditModalOpened]
    );

    const pageContainerClassName = classNames(
        'page-position lg:pt-8 xl:pt-[40px] lg:px-5 min-[1124px]:px-[40px] xl:px-[60px] flex flex-col transition-colors hide-scrollbar overflow-y-auto lg:overflow-hidden',
        {
            'bg-white': mode === PLAYBOOK_BUILDER_MODE.edit,
            'bg-neutral-50': mode === PLAYBOOK_BUILDER_MODE.view,
        }
    );

    return (
        <div className={pageContainerClassName}>
            <div className={isDesktop ? 'block' : 'hidden'}>
                <PlaybookBlocksViewTopControls
                    backLinkHref={backLinkHref}
                    setPageTopControlsHeight={setPageTopControlsHeight}
                />
            </div>

            {!isDesktop && (
                <PlaybookHeaderMobileView
                    mode={mode}
                    backLinkHref={backLinkHref}
                    playbookDetail={playbookDetail}
                    openPlaybookEditModal={openPlaybookEditModal}
                />
            )}

            <PlaybookBlocksViewIndex
                mode={mode}
                playbookDetail={playbookDetail}
                setPlaybookDetail={setPlaybookDetail}
                playbookContentData={playbookContentData}
                playbookContentChangesState={playbookContentChangesState}
                height={pageIndexHeight}
                isDesktop={isDesktop}
                openPlaybookEditModal={openPlaybookEditModal}
                changeMode={changeMode}
            />

            {isPlaybookEditModalOpened && (
                <PlaybookDetailsModal
                    mode="edit"
                    playbookData={playbookDetail}
                    setUpdatedPlaybookData={setPlaybookDetail}
                    onClose={() => setIsPlaybookEditModalOpened(false)}
                />
            )}
        </div>
    );
};

export default PlaybookBlocksView;
